import {Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ScrollHelper } from '@app/@shared/utils/scroll-helper';
import {
  HTMLReport,
  Image,
} from '@app/@shared/services/hygraph/hygraph.models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {ImageModalComponent} from '@shared/components/hygraph/image-modal/image-modal.component';
import { FeatureFlagService, ffKeys } from '@app/@shared/services/featureflag.service';

@Component({
    selector: 'app-hygraph',
    templateUrl: './hygraph.component.html',
    styleUrls: ['./hygraph.component.scss'],
    standalone: false
})
export class HygraphComponent {
  @Input() reportTitle: string;
  @Input() htmlReport: HTMLReport;
  @Input() lockedMessage: string;
  @Input() showTableOfContent: boolean;
  @Input() isContentLoading: boolean = false;
  @Input() fitWidth: boolean = false;
  @Input() isBlog: boolean = false;
  @Input() selectedStage: string;
  @ViewChild('mainSection') mainSection: ElementRef;
  @ViewChild('sectionImage', { static: true }) sectionImageTemplate: TemplateRef<any>;
  @ViewChild('sectionTable', { static: true }) sectionTableTemplate: TemplateRef<any>;
  @ViewChild('sectionText', { static: true }) sectionTextTemplate: TemplateRef<any>;
  @ViewChild('sectionVisualization', { static: true }) sectionVisualizationTemplate: TemplateRef<any>;

  @Output() stageChange = new EventEmitter<string>();

  insightfulChatComponentEnabled$ = this.ffService.getFlag(ffKeys.enableHygraphInsightfulChat);

  readonly type = {
    blog: 'hg_blog',
    report: 'hg_report',
  };

  private anchorId: string; // section anchor needed so we can scroll to specfic section of a page

  constructor(
    private readonly  modalService: NgbModal,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly ffService: FeatureFlagService,
  ) {
    this.showTableOfContent = true;
  }

  ngAfterViewInit(): void {
    // Retrive an array of section anchors so we could modify each one to our needs
    const sectionAnchor = Array.from(document.getElementsByClassName(ScrollHelper.SECTION_ANCHOR));
    sectionAnchor.forEach((anchor) => this.enhanceAnchor(anchor as HTMLAnchorElement))

    this.route.queryParamMap.subscribe((queryParams) => {
      if (queryParams.has('anchor')){
        this.scrollToElement(queryParams.get('anchor'));
      }
    });
  }

  getTemplate(content: any): TemplateRef<any> {
    if (content?.image?.length) {
      return this.sectionImageTemplate;
    } else if (content?.table?.length) {
      return this.sectionTableTemplate;
    } else if (content?.visualizations?.length) {
      return this.sectionVisualizationTemplate;
    } else {
      return this.sectionTextTemplate;
    }
  }

  openImageModal(image: Image) {
    if(window.innerWidth >= 600) {
      const modalRef = this.modalService.open(ImageModalComponent, {
        centered: true,
      });
      modalRef.componentInstance.image = image;
    } else if(image.sourceUrl) {
      window.open(image.sourceUrl, "_blank");
    }
  }

  onStageChange(value: string) {
    this.stageChange.emit(value);
  }

  /**
   * Creating correct url for section anchors for href
   * @param elementId
   * @returns url tree for anchor href
   */
  createURLTreeHref(elementId: string){
    return this.router.serializeUrl(
      this.router.createUrlTree(
        [],{
          queryParams: {anchor: elementId},
          preserveFragment: true
        }
    ));
  }

  /**
   * Provide an anchor element we add a click event listener which retireves
   * the anchor id and scroll to that section, also we are modifiying the href to
   * the correct url needed
   *
   * @param anchor
   */
  private enhanceAnchor(anchor: HTMLAnchorElement): void {
    const elementId = anchor.title;
    anchor.href = this.createURLTreeHref(elementId);

    anchor.addEventListener('click', (event: any) => {
      event.stopPropagation()
      event.preventDefault()
      if(this.anchorId === elementId){
        ScrollHelper.scrollToSection(elementId)
      } else {
        this.anchorId = elementId;
        this.router.navigate([],{
          queryParams: { anchor: elementId },
          preserveFragment: true
        })
      }
    })
  }

  /**
   * This method scroll to the element in question if and only if all images in the
   * main section has been loaded
   *
   * @param elementId element id you want to scroll to
   */
  private scrollToElement(elementId:string) {
    const images = this.mainSection.nativeElement.querySelectorAll('img');
    let imagesLoaded = 0;

    images.forEach((img) => {
      if (img.complete) {
        imagesLoaded++;
      } else {
        img.addEventListener('load', () => {
          imagesLoaded++;
          if (imagesLoaded === images.length) {
            ScrollHelper.scrollToSection(elementId);
          }
        });
      }
    });

    if (imagesLoaded === images.length) {
      ScrollHelper.scrollToSection(elementId);
    }
  }
}
